import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, Calendar, User, Eye, Heart, MessageCircle, Tag } from 'lucide-react';
import { format } from 'date-fns';
import { searchPosts } from '../firebase/firestore';
import LoadingSpinner from '../components/LoadingSpinner';

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async (searchQuery) => {
    if (!searchQuery.trim()) {
      setPosts([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const results = await searchPosts(searchQuery);
      setPosts(results);
    } catch (err) {
      setError('Failed to search posts');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSearchParams({ q: query });
    handleSearch(query);
  };

  useEffect(() => {
    if (query) {
      handleSearch(query);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Search - AI Blog</title>
        <meta name="description" content="Search through our blog posts and find the content you're looking for." />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Search Posts
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Find the content you're looking for with our powerful search functionality
            </p>
          </div>

          {/* Search Form */}
          <div className="max-w-2xl mx-auto mb-12">
            <form onSubmit={handleSubmit} className="relative">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search for posts, topics, or keywords..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white text-lg"
                />
              </div>
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-md font-medium transition-colors duration-200"
              >
                Search
              </button>
            </form>
          </div>

          {/* Search Results */}
          {loading ? (
            <LoadingSpinner text="Searching posts..." />
          ) : error ? (
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Search Error
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{error}</p>
            </div>
          ) : query ? (
            <div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Search Results
                </h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Found {posts.length} result{posts.length !== 1 ? 's' : ''} for "{query}"
                </p>
              </div>

              {posts.length === 0 ? (
                <div className="text-center py-16">
                  <SearchIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    No results found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-8">
                    Try searching with different keywords or check your spelling
                  </p>
                  <Link
                    to="/"
                    className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
                  >
                    Browse All Posts
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.map((post) => (
                    <article
                      key={post.id}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden"
                    >
                      {/* Post Image */}
                      {post.thumbnail && (
                        <div className="aspect-w-16 aspect-h-9">
                          <img
                            src={post.thumbnail}
                            alt={post.title}
                            className="w-full h-48 object-cover"
                          />
                        </div>
                      )}

                      <div className="p-6">
                        {/* Category */}
                        {post.category && (
                          <div className="flex items-center mb-3">
                            <Tag className="h-4 w-4 text-primary-600 dark:text-primary-400 mr-2" />
                            <span className="text-sm text-primary-600 dark:text-primary-400 font-medium">
                              {post.category}
                            </span>
                          </div>
                        )}

                        {/* Title */}
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 line-clamp-2">
                          <Link
                            to={`/post/${post.slug}`}
                            className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                          >
                            {post.title}
                          </Link>
                        </h3>

                        {/* Excerpt */}
                        <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-3">
                          {post.excerpt || post.content?.substring(0, 150) + '...'}
                        </p>

                        {/* Meta Info */}
                        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
                          <div className="flex items-center">
                            <User className="h-4 w-4 mr-1" />
                            <span>{post.author || 'Admin'}</span>
                          </div>
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            <span>
                              {post.createdAt ? format(post.createdAt.toDate(), 'MMM dd, yyyy') : 'Recently'}
                            </span>
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center">
                              <Eye className="h-4 w-4 mr-1" />
                              <span>{post.views || 0}</span>
                            </div>
                            <div className="flex items-center">
                              <Heart className="h-4 w-4 mr-1" />
                              <span>{post.likes || 0}</span>
                            </div>
                            <div className="flex items-center">
                              <MessageCircle className="h-4 w-4 mr-1" />
                              <span>{post.comments || 0}</span>
                            </div>
                          </div>
                          <Link
                            to={`/post/${post.slug}`}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors duration-200"
                          >
                            Read More →
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-16">
              <SearchIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Start Searching
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Enter a search term above to find relevant posts
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Search;
